import * as React from 'react';
import './button.css';

/**
 * Button (CC5): the landing pill.
 *
 * Variants
 *   default / primary  flat teal-600, teal-700 on hover, soft teal glow
 *   secondary / outline white with a teal-200 hairline, teal-50 on hover
 *   ghost              no chrome, teal tint on hover
 *   destructive        red, for genuinely destructive actions only
 *                      (delete, remove, revoke). "End" and "Stop" are primary.
 *
 * Sizes: lg 44px, md 40px, sm 36px. Under 640px every size grows to 44px
 * so tap targets stay honest on touch.
 *
 * Rendering as a link (AE-req-04): pass `to` for a React Router <Link> or
 * `href` for a plain anchor and the same classes, motion and sizes apply -
 * no hand-applied `lnr-btn` classes on Links any more.
 *
 *   <Button to="/login" variant="secondary">Back to login</Button>
 *   <Button href="https://..." target="_blank" rel="noreferrer">Docs</Button>
 *
 * Press feedback (PP-06): the 1px hover lift and 2% press settle are CSS
 * transforms on the pill (button.css), not a framer motion.button per
 * instance - 55 screens mount dozens of these per navigation, and each
 * framer element cost a visual-element, a reduced-motion subscription and
 * hover/tap listeners. The curve matches the kit's SPRING_FAST and the
 * global reduced-motion rule drops the transition.
 */
type Variant = 'default' | 'primary' | 'secondary' | 'ghost' | 'outline' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

type LinkExtras = {
  /** Render as a React Router <Link> to this route. */
  to?: string;
  /** Render as a plain anchor with this href. */
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  download?: string | boolean;
  replace?: boolean;
  state?: unknown;
};

type ButtonProps = React.ComponentPropsWithRef<'button'> & {
  variant?: Variant;
  size?: Size;
} & LinkExtras;

const VARIANT_CLASS: Record<Variant, string> = {
  default: 'primary',
  primary: 'primary',
  secondary: 'secondary',
  outline: 'secondary',
  ghost: 'ghost',
  destructive: 'destructive',
};

/**
 * React Router's Link is resolved lazily on the first render that passes
 * `to`, so screens (and test suites) that only use buttons or plain anchors
 * never load react-router-dom. The result is cached at module level so the
 * component identity is stable across renders.
 */
let linkCache: React.ComponentType<any> | null = null;
function getLink(): React.ComponentType<any> {
  if (!linkCache) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { Link } = require('react-router-dom') as typeof import('react-router-dom');
    linkCache = Link as unknown as React.ComponentType<any>;
  }
  return linkCache;
}

export function Button({
  children,
  variant = 'default',
  size = 'md',
  className = '',
  style,
  to,
  href,
  target,
  rel,
  download,
  replace,
  state,
  ...props
}: ButtonProps) {
  const cls = `lnr-btn lnr-btn--${VARIANT_CLASS[variant]} lnr-btn--${size} ${className}`.trim();

  if (to !== undefined || href !== undefined) {
    // Anchors have no `disabled`; expose it as aria-disabled and drop the
    // click so the link still reads and styles like a disabled pill.
    const { disabled, onClick, ...anchorProps } = props as Record<string, unknown> & {
      disabled?: boolean;
      onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    };
    delete (anchorProps as Record<string, unknown>).type;
    const shared = {
      className: cls,
      style,
      target,
      rel: rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined),
      download,
      'aria-disabled': disabled || undefined,
      tabIndex: disabled ? -1 : undefined,
      onClick: disabled ? (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault() : onClick,
    };
    if (to !== undefined) {
      const Link = getLink();
      return (
        <Link to={to} replace={replace} state={state} {...(anchorProps as any)} {...shared}>
          {children}
        </Link>
      );
    }
    return (
      <a href={href} {...(anchorProps as any)} {...shared}>
        {children}
      </a>
    );
  }

  return (
    <button className={cls} style={style} {...props}>
      {children}
    </button>
  );
}
